import { deepClone } from '@src/utility/deepClone';
import { v4 as uuid } from 'uuid';

import type { ILayoutSegment, TrackTransforms } from './types';

/**
 * LayoutSegment class
 * Represents a layout configuration
 * where specific tracks are arranged according to a layout template
 */
export class LayoutSegment implements ILayoutSegment {
  private _id: string;
  private _layoutTemplateId: string;
  private _bindings: Record<string, TrackTransforms>;

  constructor(
    props: Partial<ILayoutSegment> & {
      layoutTemplateId: string;
    },
  ) {
    this._id = props.id ?? uuid();
    this._layoutTemplateId = props.layoutTemplateId;
    this._bindings = props.bindings ?? {};
  }

  // Getters
  get id(): string {
    return this._id;
  }

  get layoutTemplateId(): string {
    return this._layoutTemplateId;
  }

  get bindings(): Record<string, TrackTransforms> {
    return this._bindings;
  }

  // Setters
  setLayoutTemplateId(templateId: string): void {
    this._layoutTemplateId = templateId;
  }

  /**
   * Binds a track to a specific slot in the layout
   */
  bindTrack(slotName: string, trackTransforms: TrackTransforms): void {
    this._bindings[slotName] = trackTransforms;
  }

  /**
   * Unbinds a track from a slot
   */
  unbindTrack(slotName: string): boolean {
    if (slotName in this._bindings) {
      delete this._bindings[slotName];
      return true;
    }
    return false;
  }

  /**
   * Gets track transforms for a specific slot
   */
  getTrackTransforms(slotName: string): TrackTransforms | undefined {
    return this._bindings[slotName];
  }

  /**
   * Serializes to a plain JSON-safe object.
   * Bindings are deep-cloned so callers (e.g. structuredClone) never
   * receive MobX proxy references nested inside TrackTransforms.
   */
  toJSON(): ILayoutSegment {
    return {
      id: this._id,
      layoutTemplateId: this._layoutTemplateId,
      bindings: deepClone(this._bindings),
    };
  }

  /**
   * Creates a LayoutSegment from JSON
   */
  static fromJSON(json: ILayoutSegment): LayoutSegment {
    return new LayoutSegment(json);
  }

  clone(): LayoutSegment {
    return LayoutSegment.fromJSON(this.toJSON());
  }
}
